/**
 * Experience item → section 3 · Credits: who gets this item on their record.
 * One bordered group per party type (Borrowers, Entities, General
 * contractors), each a checklist of the loan's parties with an initials
 * avatar, the name, and one quiet line about who they are.
 *
 * - "N of M selected" with **Use suggested** and **Clear** across all groups,
 *   and **Select all / Clear** per group.
 * - **Suggested** marks parties the item most likely belongs to, from what the
 *   dialog already knows: the borrower it was opened from, and an entity whose
 *   name matches the schedule section (holding entity) typed on the item.
 *   With nothing to suggest, neither the tags nor the button show.
 * - **Same name, check license** flags two contractors with the same name, so
 *   the reviewer picks by license rather than by name.
 */
import { useMemo } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { amber, slate } from "../../../theme/colors";
import { FORM, formSx, px } from "../../../theme/formStyles";
import { ASSOCIATION_TYPES, fmtPoints } from "./experienceConstants";

const GROUPS = [
  { type: ASSOCIATION_TYPES.BORROWERS, title: "Borrowers" },
  { type: ASSOCIATION_TYPES.ENTITIES, title: "Entities" },
  { type: ASSOCIATION_TYPES.GENERAL_CONTRACTORS, title: "General contractors" },
];

const norm = (s) => String(s || "").trim().toLowerCase();

const initials = (name) => {
  const words = String(name || "")
    .replace(/[^A-Za-z0-9 ]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  return ((words[0]?.[0] || "") + (words[1]?.[0] || "")).toUpperCase() || "?";
};

const pts = (p) =>
  Number(p.verifiedExperienceTotal) > 0
    ? `${fmtPoints(Number(p.verifiedExperienceTotal))} verified pts`
    : null;

/** The quiet line under a party's name — only facts the loan carries. */
const subline = (p, byId) => {
  const r = p.raw || {};
  if (p.type === ASSOCIATION_TYPES.BORROWERS) {
    const role = r.isPrimaryContactForLoan
      ? "Primary contact"
      : r.isGuarantor
        ? "Guarantor"
        : "Borrower";
    return [role, pts(p)].filter(Boolean).join(" · ");
  }
  if (p.type === ASSOCIATION_TYPES.ENTITIES) {
    const parent = r.parentEntityId ? byId.get(String(r.parentEntityId)) : null;
    const held = parent
      ? `${r.ownershipPercentInParent != null ? `${r.ownershipPercentInParent}% held` : "Held"} by ${parent.name}`
      : r.entityType || "Entity";
    return [held, pts(p)].filter(Boolean).join(" · ");
  }
  const a = r.address || {};
  const place = [a.county, a.state].filter(Boolean).join(", ");
  return [
    r.licenseNumber ? `Lic. #${r.licenseNumber}` : "No license number on file",
    place || null,
    pts(p),
  ]
    .filter(Boolean)
    .join(" · ");
};

const Tag = ({ tone = "teal", children }) => (
  <Box
    component="span"
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: "8px",
      py: "1px",
      borderRadius: "999px",
      fontSize: 12,
      fontWeight: 500,
      lineHeight: "18px",
      whiteSpace: "nowrap",
      ...(tone === "amber"
        ? { bgcolor: amber.light, color: amber.chipText, border: `1px solid ${amber.light}` }
        : {
            bgcolor: FORM.color.accentTint,
            color: FORM.color.accentStrong,
            border: `1px solid ${FORM.color.accentBorder}`,
          }),
    }}
  >
    {children}
  </Box>
);

Tag.propTypes = { tone: PropTypes.string, children: PropTypes.node };

const LinkButton = ({ children, onClick, muted, disabled }) => (
  <Box
    component="button"
    type="button"
    onClick={onClick}
    disabled={disabled}
    sx={{
      p: 0,
      border: 0,
      bgcolor: "transparent",
      font: "inherit",
      fontSize: 13,
      fontWeight: 500,
      color: muted ? FORM.color.ink3 : FORM.color.accent,
      cursor: disabled ? "default" : "pointer",
      opacity: disabled ? 0.5 : 1,
      "&:hover": disabled ? undefined : { textDecoration: "underline" },
    }}
  >
    {children}
  </Box>
);

LinkButton.propTypes = {
  children: PropTypes.node,
  onClick: PropTypes.func,
  muted: PropTypes.bool,
  disabled: PropTypes.bool,
};

const ExperienceCreditsPicker = ({ parties, value, onChange, suggestedIds }) => {
  const byId = useMemo(() => new Map(parties.map((p) => [p.id, p])), [parties]);
  const selected = useMemo(
    () => new Set(GROUPS.flatMap((g) => value[g.type] || [])),
    [value],
  );
  const suggested = useMemo(
    () => new Set((suggestedIds || []).filter((id) => byId.has(id))),
    [suggestedIds, byId],
  );
  // Two contractors (or two anything) with one name: pick by the details.
  const dupNames = useMemo(() => {
    const seen = new Map();
    parties.forEach((p) => {
      const k = `${p.type}|${norm(p.name)}`;
      seen.set(k, (seen.get(k) || 0) + 1);
    });
    return seen;
  }, [parties]);

  const setGroup = (type, ids) => onChange(type, ids);
  const toggle = (p) => {
    const cur = value[p.type] || [];
    setGroup(p.type, cur.includes(p.id) ? cur.filter((x) => x !== p.id) : [...cur, p.id]);
  };
  const useSuggested = () =>
    GROUPS.forEach((g) => {
      const add = parties.filter((p) => p.type === g.type && suggested.has(p.id)).map((p) => p.id);
      if (add.length) setGroup(g.type, [...new Set([...(value[g.type] || []), ...add])]);
    });
  const clearAll = () => GROUPS.forEach((g) => (value[g.type] || []).length && setGroup(g.type, []));
  const allSuggestedOn = [...suggested].every((id) => selected.has(id));

  return (
    <Flx column sx={{ gap: px(FORM.s.s3) }}>
      <Flx ac jb g={2}>
        <Txt sx={{ fontSize: 14, color: FORM.color.ink2 }}>
          <Box component="span" sx={{ fontWeight: 600, color: FORM.color.ink }}>
            {selected.size}
          </Box>{" "}
          of {parties.length} selected
        </Txt>
        <Flx ac sx={{ gap: px(FORM.s.s4) }}>
          {suggested.size ? (
            <LinkButton onClick={useSuggested} disabled={allSuggestedOn}>
              Use suggested
            </LinkButton>
          ) : null}
          <LinkButton muted onClick={clearAll} disabled={!selected.size}>
            Clear
          </LinkButton>
        </Flx>
      </Flx>

      {GROUPS.map((g) => {
        const rows = parties.filter((p) => p.type === g.type);
        if (!rows.length) return null;
        const ids = value[g.type] || [];
        const all = rows.every((p) => ids.includes(p.id));
        return (
          <Box
            key={g.type}
            role="group"
            aria-label={g.title}
            sx={{
              border: `1px solid ${FORM.color.divider}`,
              borderRadius: "10px",
              overflow: "hidden",
              bgcolor: "#FFFFFF",
            }}
          >
            <Flx
              ac
              jb
              sx={{
                px: px(FORM.s.s4),
                py: "10px",
                bgcolor: slate[50],
                borderBottom: `1px solid ${FORM.color.divider}`,
              }}
            >
              <Txt
                sx={{
                  ...formSx.groupLabel,
                  mb: 0,
                  textTransform: "uppercase",
                  letterSpacing: ".06em",
                }}
              >
                {g.title}
              </Txt>
              <LinkButton onClick={() => setGroup(g.type, all ? [] : rows.map((p) => p.id))}>
                {all ? "Clear" : "Select all"}
              </LinkButton>
            </Flx>
            {rows.map((p, i) => {
              const on = ids.includes(p.id);
              const isGc = p.type === ASSOCIATION_TYPES.GENERAL_CONTRACTORS;
              const dup = dupNames.get(`${p.type}|${norm(p.name)}`) > 1;
              return (
                <Flx
                  key={p.id}
                  component="label"
                  ac
                  sx={{
                    gap: px(FORM.s.s3),
                    px: px(FORM.s.s4),
                    py: "10px",
                    cursor: "pointer",
                    borderTop: i ? `1px solid ${FORM.color.divider}` : "none",
                    bgcolor: on ? FORM.color.accentTint : "transparent",
                    transition: "background-color .12s",
                    "&:hover": { bgcolor: on ? FORM.color.accentTint : slate[50] },
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={on}
                    onChange={() => toggle(p)}
                    inputProps={{ "aria-label": p.name }}
                    sx={{ p: 0.5, m: -0.5 }}
                  />
                  <Flx
                    center
                    sx={{
                      width: 36,
                      height: 36,
                      flexShrink: 0,
                      borderRadius: p.type === ASSOCIATION_TYPES.BORROWERS ? "50%" : "10px",
                      bgcolor: isGc ? amber.light : slate[100],
                      color: isGc ? amber.chipText : FORM.color.ink2,
                      fontSize: 13,
                      fontWeight: 600,
                    }}
                  >
                    {initials(p.name)}
                  </Flx>
                  <Flx column sx={{ minWidth: 0, flex: 1 }}>
                    <Flx ac wrap sx={{ gap: px(FORM.s.s2) }}>
                      <Txt sx={{ fontSize: 14, fontWeight: 500, color: FORM.color.ink }}>{p.name}</Txt>
                      {suggested.has(p.id) ? <Tag>Suggested</Tag> : null}
                      {dup ? (
                        <Tag tone="amber">{isGc ? "Same name, check license" : "Same name, check details"}</Tag>
                      ) : null}
                    </Flx>
                    <Txt sx={{ ...formSx.meta, color: FORM.color.ink3 }}>{subline(p, byId)}</Txt>
                  </Flx>
                </Flx>
              );
            })}
          </Box>
        );
      })}
    </Flx>
  );
};

ExperienceCreditsPicker.propTypes = {
  /** listLoanParties(loan) — `{ id, type, name, verifiedExperienceTotal, raw }`. */
  parties: PropTypes.array.isRequired,
  /** `{ borrowers: [id], entities: [id], generalContractors: [id] }`. */
  value: PropTypes.object.isRequired,
  /** (type, ids) — one association list at a time. */
  onChange: PropTypes.func.isRequired,
  suggestedIds: PropTypes.array,
};

export default ExperienceCreditsPicker;
